import { BarkerEventListing } from '../../types';
import { GridApi, ICellRendererParams, IRowNode } from 'ag-grid-community';
import { useCallback, useEffect, useRef } from 'react';
import { useBulkState } from '../../data/Bulk.state';
import { BNGridCheckbox } from '../../components/GridCheckbox/GridCheckbox';
import { useAtomValue } from 'jotai';
import { selectedTenantListingIdAtom } from '../../data/atoms';
import { Text } from '@mantine/core';
import { TenantIdListingId } from '../../models/tenantIdListingId';
import { range } from '../../utils/price-utils';

type Props = {
  listingId: string;
  tenantId: string;
  isManualPricingOnlyPlan: boolean;
  isPrimaryListing?: boolean;
  bulkMode?: boolean;
  ruleCount: number;
  level: number;
  node: IRowNode<BarkerEventListing>;
  api: GridApi<BarkerEventListing>;
  registerRowDragger: ICellRendererParams['registerRowDragger'];
  isListingOnHold: boolean;
};

export const DragCell = ({
  tenantId,
  listingId,
  isManualPricingOnlyPlan,
  isPrimaryListing: primaryListing,
  isListingOnHold,
  node,
  api,
  level,
  ruleCount,
  bulkMode = false,
  registerRowDragger,
}: Props) => {
  const { markedListings, setMarkedListings } = useBulkState('markedListings', 'setMarkedListings');
  const selectedTenantListingId = useAtomValue(selectedTenantListingIdAtom);
  const currentTenantIdListingId = TenantIdListingId.fromString(`${tenantId}|${listingId}`);

  const dragRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dragRef.current && !isManualPricingOnlyPlan) {
      registerRowDragger(dragRef.current, 0);
    }
  }, [isManualPricingOnlyPlan, registerRowDragger]);

  const toggleChecked = useCallback(
    (event: React.MouseEvent<HTMLInputElement>) => {
      if (event.shiftKey) {
        const lastIndex = api.getRowNode(selectedTenantListingId.toString())?.rowIndex!;
        const currentIndex = node.rowIndex!;
        const listingIdsInRange = range(lastIndex, currentIndex)
          .map((i) => api.getDisplayedRowAtIndex(i)?.data?.tenantIdListingId)
          .filter((x) => x != null && !x.endsWith('sales')) as string[];
        setMarkedListings((prev) => [...prev, ...listingIdsInRange]);
      } else {
        setMarkedListings(currentTenantIdListingId.toString());
      }
    },
    [api, currentTenantIdListingId, node.rowIndex, selectedTenantListingId, setMarkedListings],
  );

  if ((bulkMode && listingId.includes('sales')) || isListingOnHold) {
    return null;
  }

  if (bulkMode && level === 1) {
    if (selectedTenantListingId.toString() === currentTenantIdListingId.toString()) {
      return <BNGridCheckbox checked readOnly pt={0} pl={4} size="xs" inverted group={ruleCount > 1} />;
    }
    const isMarked = markedListings.includes(currentTenantIdListingId.toString());
    return (
      <BNGridCheckbox
        // The checkbox is controlled by `markedListings` but driven via onClick (not onChange), so
        // React's controlled-input value tracker can skip syncing the DOM `.checked` of the box you
        // just clicked — leaving it visually unchecked even though the listing is marked. Keying the
        // element by its checked state forces a fresh input whenever it flips, guaranteeing the
        // visual matches state. See onClick handler `toggleChecked`.
        key={isMarked ? 'marked' : 'unmarked'}
        onClick={toggleChecked}
        readOnly
        checked={isMarked}
        pt={0}
        pl={4}
        size="xs"
        inverted={primaryListing}
        group={ruleCount > 1}
        inlay={
          ruleCount > 1 ? (
            <Text fz={11} c="gray.4" style={{ pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {ruleCount}
            </Text>
          ) : undefined
        }
        aria-label={`Mark listing ${listingId}`}
      />
    );
  }
  if (ruleCount && level === 1 && ruleCount > 1) {
    return (
      <button
        type="button"
        tabIndex={-1}
        aria-label={`Expand group ${listingId}`}
        onClick={(e) => {
          e.preventDefault();
          node.setExpanded(!node?.expanded);
        }}
        className="custom-icon-grip"
      >
        {ruleCount}
      </button>
    );
  }
  if (isManualPricingOnlyPlan) {
    return (
      <div className="ag-cell-wrapper wow no-row-drag-handle" role="presentation">
        <span className="ag-cell-value" />
      </div>
    );
  }
  return (
    <div className="ag-cell-wrapper wow" role="presentation">
      <div className="ag-drag-handle ag-row-drag" ref={dragRef}>
        <span className="ag-icon ag-icon-grip" />
      </div>
      <span className="ag-cell-value" />
    </div>
  );
};
